import { useEffect, useRef, useState } from "react";
import { Menu, X, Headset, LayoutTemplate, PlayCircle, Code2, ChevronLeft, User, Crown } from "lucide-react";
import { api } from "../lib/api";
import { AccountOut, MembershipTier } from "../types";
import { openExternalLink } from "../lib/telegram";

const SUPPORT_LINK = "https://t.me/hhs9h";
const DEVELOPER_LINK = "https://t.me/zzzzd";

const TIER_LABELS: Record<MembershipTier, string> = {
  free: "مجانية",
  pro: "Pro",
  vip: "VIP",
};

function formatExpiry(expiresAt: number | null): string {
  if (!expiresAt) return "بدون تاريخ انتهاء";
  const date = new Date(expiresAt * 1000);
  return `تنتهي في ${date.toLocaleDateString("ar-EG", { year: "numeric", month: "long", day: "numeric" })}`;
}

export function AppHeader() {
  const [logoFailed, setLogoFailed] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [account, setAccount] = useState<AccountOut | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const avatarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then(setAccount)
      .catch(() => undefined);
  }, []);

  const menuItems = [
    { label: "حساب الدعم", icon: Headset, onClick: () => openExternalLink(SUPPORT_LINK) },
    ...(account?.updates_channel_link
      ? [{ label: "قناة القوالب", icon: LayoutTemplate, onClick: () => openExternalLink(account.updates_channel_link) }]
      : []),
    ...(account?.tutorial_video_url
      ? [{ label: "شرح البوت", icon: PlayCircle, onClick: () => openExternalLink(account.tutorial_video_url) }]
      : []),
    { label: "المطوّر", icon: Code2, onClick: () => openExternalLink(DEVELOPER_LINK) },
  ];

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-1 shrink-0" dir="ltr">
        {/* Hamburger menu - far left, matching the reference layout.
            dir="ltr" above forces this row's left-to-right positioning
            regardless of the app's overall RTL direction - without it,
            RTL flex ordering would auto-reverse this to the right. */}
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="القائمة"
          className="w-8 h-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0"
        >
          <Menu size={16} className="text-tg-hint" strokeWidth={2} />
        </button>

        {/* Logo + title, right after the menu button */}
        <div className="flex items-center gap-2 me-auto">
          {!logoFailed ? (
            <img
              src="/logo.png"
              alt=""
              onError={() => setLogoFailed(true)}
              className="w-8 h-8 rounded-xl object-cover shrink-0"
            />
          ) : (
            <span className="w-8 h-8 rounded-xl bg-white/10 shrink-0" />
          )}
          <span className="text-base font-extrabold tracking-wide text-tg-text" dir="ltr">
            STICKER HUB
          </span>
        </div>

        {/* Plan badge + avatar, far right */}
        {account && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-tg-text">
              <Crown size={13} strokeWidth={2.25} className="text-tg-hint" />
              {TIER_LABELS[account.membership.tier]}
            </span>

            <button
              ref={avatarRef}
              type="button"
              onClick={() => setProfilePopupOpen((v) => !v)}
              aria-label="الملف الشخصي"
              className="relative shrink-0"
            >
              {account.photo_url && !photoFailed ? (
                <img
                  src={account.photo_url}
                  alt=""
                  onError={() => setPhotoFailed(true)}
                  className="w-8 h-8 rounded-full object-cover border border-white/15"
                />
              ) : (
                <span className="w-8 h-8 rounded-full border border-white/15 bg-white/5 flex items-center justify-center">
                  <User size={16} strokeWidth={2} className="text-tg-hint" />
                </span>
              )}
              <span className="absolute -bottom-0.5 -end-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-tg-bg" />
            </button>
          </div>
        )}
      </div>

      {/* Profile popover - name, tier, usage limit */}
      {profilePopupOpen && account && (
        <div className="fixed inset-0 z-50" onClick={() => setProfilePopupOpen(false)}>
          <div
            className="absolute top-14 end-4 w-60 rounded-2xl bg-tg-bg border border-white/10 shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 p-3.5 border-b border-white/10">
              {account.photo_url && !photoFailed ? (
                <img src={account.photo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-white/15" />
              ) : (
                <span className="w-10 h-10 rounded-full border border-white/15 bg-white/5 flex items-center justify-center">
                  <User size={18} strokeWidth={2} className="text-tg-hint" />
                </span>
              )}
              <div className="min-w-0">
                <span className="block text-sm font-bold text-tg-text truncate">{account.first_name}</span>
                <span className="block text-xs text-tg-hint">{TIER_LABELS[account.membership.tier]}</span>
              </div>
            </div>
            <div className="p-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-tg-hint">استخدامك اليوم</span>
                <span dir="ltr" className="text-xs font-semibold text-tg-text tabular-nums">
                  {account.daily_used} / {account.daily_limit ?? "∞"}
                </span>
              </div>
              {account.daily_limit !== null && (
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-tg-button"
                    style={{ width: `${Math.min(100, (account.daily_used / account.daily_limit) * 100)}%` }}
                  />
                </div>
              )}
              {account.membership.tier !== "free" && (
                <span className="block text-xs text-tg-hint mt-2.5">{formatExpiry(account.membership.expires_at)}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hamburger side menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative ms-auto h-full w-[78%] max-w-[300px] bg-tg-bg border-s border-white/10 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
          >
            <div className="flex items-center justify-between px-4 pb-3 border-b border-white/10">
              <span className="text-sm font-bold text-tg-text">القائمة</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="إغلاق"
                className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center"
              >
                <X size={16} className="text-tg-hint" strokeWidth={2} />
              </button>
            </div>
            <div className="flex flex-col py-2">
              {menuItems.map(({ label, icon: Icon, onClick }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    onClick();
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3.5 text-start active:bg-white/5"
                >
                  <span className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-tg-hint" strokeWidth={1.75} />
                  </span>
                  <span className="flex-1 text-sm text-tg-text">{label}</span>
                  <ChevronLeft size={16} className="text-tg-hint" strokeWidth={2} />
                </button>
              ))}
            </div>

            {/* Bottom card - pinned to the drawer's bottom. free/pro see
                their daily usage; vip sees their subscription expiry
                instead, same professional card style either way. */}
            {account && account.membership.tier !== "vip" && (
              <div
                className="mt-auto mx-4 mb-4 p-3.5 rounded-2xl bg-white/[0.04] border border-white/10"
                style={{ marginBottom: "max(1rem, env(safe-area-inset-bottom))" }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-tg-text">استخدامك اليوم</span>
                  <span dir="ltr" className="text-xs font-bold text-tg-text tabular-nums">
                    {account.daily_used} / {account.daily_limit ?? "∞"}
                  </span>
                </div>
                {account.daily_limit !== null && (
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-tg-button transition-all"
                      style={{ width: `${Math.min(100, (account.daily_used / account.daily_limit) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {account && account.membership.tier === "vip" && (
              <div
                className="mt-auto mx-4 mb-4 p-3.5 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center gap-3"
                style={{ marginBottom: "max(1rem, env(safe-area-inset-bottom))" }}
              >
                <span className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Crown size={16} strokeWidth={1.75} className="text-tg-hint" />
                </span>
                <div className="min-w-0">
                  <span className="block text-xs font-semibold text-tg-text">عضوية VIP</span>
                  <span className="block text-xs text-tg-hint mt-0.5">{formatExpiry(account.membership.expires_at)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
